/**
 * Identidade única da venda no Operador (`externalId`).
 *
 * BUG 1 (QA): sem externalId o legado faz `Log.findOne({ id_passbook, externalId: null })`
 * e a role Operator recebe 422 `{ status:'105', error:'null transaction has already been made' }`
 * (user.js ~9074, ~9219-9267). Validators no dash antigo escapavam do guard.
 *
 * Por isso o Operador v2 **sempre** envia um `externalId` não-nulo.
 *
 * Cupom / NFC-e NÃO são chave de idempotência (no dash antigo iam só em addFields).
 * Usar o cupom como externalId fazia o mesmo código colidir entre leads (105 / duplicate ignored).
 * Aqui: sempre `op-<id>` único por confirmação; cupom fica em `numberCodCupom` (addFields).
 *
 * resgateNaoPontua (user.js ~5151/6882): no addPoints o backend transforma externalId em
 * REGEX case-insensitive — por isso sanitizamos para `[A-Za-z0-9-]`. O leg de resgate usa
 * `${base}-resgate` para o dedup exato não colidir, e o regex `/base/i` ainda casa o sufixo.
 */

const SAFE_EXTERNAL_ID = /[^A-Za-z0-9-]/g;

/** Remove caracteres que quebrariam o RegExp do backend (cupom em addFields / base op-). */
export function sanitizePurchaseCode(raw: string): string {
  return String(raw ?? '')
    .trim()
    .replace(SAFE_EXTERNAL_ID, '')
    .slice(0, 64);
}

function shortOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`.slice(0, 12);
}

/** Novo id de venda do Operador — nunca vazio, nunca cupom/NFC-e. */
export function newOperatorExternalId(): string {
  return `op-${shortOpId()}`;
}

/**
 * Resolve o externalId da venda.
 * Args legados (cupom / NFC-e) são ignorados de propósito — não são chave de dedup.
 * @deprecated Prefer `newOperatorExternalId()`; mantido para call sites existentes.
 */
export function resolveExternalId(_typedCode?: string | null, _nfceKey?: string | null): string {
  return newOperatorExternalId();
}

/** Leg de resgate: mesmo base + sufixo (dedup exato ≠ lançamento; regex resgateNaoPontua casa). */
export function redemptionExternalId(base: string): string {
  const clean = sanitizePurchaseCode(base) || newOperatorExternalId();
  if (clean.endsWith('-resgate')) return clean;
  return `${clean}-resgate`;
}
